import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, Plus, Trash2, GripVertical, Zap, ChevronDown, ChevronUp, Webhook, MessageSquare, Mail, Bell, GitBranch, Settings2 } from 'lucide-react';
import { Card, Button, Input, useToast, Label, Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@caruso/ui';
import { boardsService, Board, BoardStep, CreateBoardStepDto, UpdateBoardStepDto, BoardStepAction, BoardStepActionType, CreateBoardStepActionDto } from '@/services/boards.service';
import { flowsService, Flow } from '@/services/flows.service';
import { usersService } from '@/services/users.service';

const COLORS = [
  '#6366f1', // Indigo
  '#8b5cf6', // Violet
  '#ec4899', // Pink
  '#ef4444', // Red
  '#f97316', // Orange
  '#eab308', // Yellow
  '#22c55e', // Green
  '#14b8a6', // Teal
  '#06b6d4', // Cyan
  '#3b82f6', // Blue
];

const ACTION_TYPES: { type: BoardStepActionType; label: string; icon: any; color: string }[] = [
  { type: 'WEBHOOK', label: 'Webhook', icon: Webhook, color: 'text-purple-400' },
  { type: 'WHATSAPP', label: 'WhatsApp', icon: MessageSquare, color: 'text-green-400' },
  { type: 'EMAIL', label: 'Email', icon: Mail, color: 'text-blue-400' },
  { type: 'NOTIFICATION', label: 'Notificacao', icon: Bell, color: 'text-amber-400' },
  { type: 'FLOW', label: 'Disparar Fluxo', icon: GitBranch, color: 'text-cyan-400' },
];

interface ActionFormData {
  id?: string;
  actionType: BoardStepActionType;
  name: string;
  description: string;
  active: boolean;
  delayMinutes: number;
  config: Record<string, any>;
}

interface StepFormData {
  id?: string;
  name: string;
  description: string;
  color: string;
  triggerFlowId: string | null;
  triggerFlowDelay: number;
  isNew?: boolean;
  isExpanded?: boolean;
}

export function BoardFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const isEditing = !!id;

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [color, setColor] = useState('#6366f1');
  const [active, setActive] = useState(true);
  const [steps, setSteps] = useState<StepFormData[]>([]);

  // Actions dialog state
  const [actionsDialogOpen, setActionsDialogOpen] = useState(false);
  const [selectedStepForActions, setSelectedStepForActions] = useState<string | null>(null);
  const [stepActions, setStepActions] = useState<BoardStepAction[]>([]);
  const [actionForm, setActionForm] = useState<ActionFormData>({
    actionType: 'NOTIFICATION',
    name: '',
    description: '',
    active: true,
    delayMinutes: 0,
    config: {},
  });
  const [editingAction, setEditingAction] = useState<string | null>(null);

  // Fetch board data if editing
  const { data: board, isLoading: isLoadingBoard } = useQuery({
    queryKey: ['board', id],
    queryFn: () => boardsService.getById(id!),
    enabled: isEditing,
  });

  // Fetch flows for trigger selection
  const { data: flows } = useQuery({
    queryKey: ['flows'],
    queryFn: () => flowsService.getAll(),
  });

  // Fetch users for notification targets
  const { data: usersData } = useQuery({
    queryKey: ['users'],
    queryFn: () => usersService.getAll(),
    enabled: actionsDialogOpen,
  });

  // Fetch actions for selected step
  const { data: actionsData, refetch: refetchActions } = useQuery({
    queryKey: ['step-actions', selectedStepForActions],
    queryFn: () => boardsService.getStepActions(selectedStepForActions!),
    enabled: !!selectedStepForActions && actionsDialogOpen,
  });

  useEffect(() => {
    if (actionsData) {
      setStepActions(actionsData);
    }
  }, [actionsData]);

  // Action mutations
  const createActionMutation = useMutation({
    mutationFn: (data: CreateBoardStepActionDto) => boardsService.createStepAction(data),
    onSuccess: () => {
      refetchActions();
      resetActionForm();
      toast({ title: 'Acao criada com sucesso!', variant: 'success' });
    },
    onError: () => {
      toast({ title: 'Erro ao criar acao', variant: 'destructive' });
    },
  });

  const updateActionMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => boardsService.updateStepAction(id, data),
    onSuccess: () => {
      refetchActions();
      resetActionForm();
      toast({ title: 'Acao atualizada com sucesso!', variant: 'success' });
    },
    onError: () => {
      toast({ title: 'Erro ao atualizar acao', variant: 'destructive' });
    },
  });

  const deleteActionMutation = useMutation({
    mutationFn: (id: string) => boardsService.deleteStepAction(id),
    onSuccess: () => {
      refetchActions();
      toast({ title: 'Acao removida com sucesso!', variant: 'success' });
    },
    onError: () => {
      toast({ title: 'Erro ao remover acao', variant: 'destructive' });
    },
  });

  const resetActionForm = () => {
    setActionForm({
      actionType: 'NOTIFICATION',
      name: '',
      description: '',
      active: true,
      delayMinutes: 0,
      config: {},
    });
    setEditingAction(null);
  };

  const openActionsDialog = (stepId: string) => {
    setSelectedStepForActions(stepId);
    setActionsDialogOpen(true);
    resetActionForm();
  };

  const closeActionsDialog = () => {
    setActionsDialogOpen(false);
    setSelectedStepForActions(null);
    setStepActions([]);
    resetActionForm();
  };

  const handleSaveAction = () => {
    if (!selectedStepForActions || !actionForm.name.trim()) {
      toast({ title: 'Preencha o nome da acao', variant: 'destructive' });
      return;
    }

    const data = {
      stepId: selectedStepForActions,
      actionType: actionForm.actionType,
      name: actionForm.name,
      description: actionForm.description || undefined,
      active: actionForm.active,
      delayMinutes: actionForm.delayMinutes,
      config: actionForm.config,
    };

    if (editingAction) {
      updateActionMutation.mutate({ id: editingAction, data });
    } else {
      createActionMutation.mutate(data);
    }
  };

  const handleEditAction = (action: BoardStepAction) => {
    setEditingAction(action.id);
    setActionForm({
      actionType: action.actionType,
      name: action.name,
      description: action.description || '',
      active: action.active,
      delayMinutes: action.delayMinutes,
      config: action.config || {},
    });
  };

  const handleDeleteAction = (actionId: string) => {
    if (confirm('Tem certeza que deseja remover esta acao?')) {
      deleteActionMutation.mutate(actionId);
    }
  };

  // Initialize form data when board is loaded
  useEffect(() => {
    if (board) {
      setName(board.name);
      setDescription(board.description || '');
      setColor(board.color || '#6366f1');
      setActive(board.active);
      setSteps(
        board.steps?.map((step) => ({
          id: step.id,
          name: step.name,
          description: step.description || '',
          color: step.color || '#6366f1',
          triggerFlowId: step.triggerFlowId,
          triggerFlowDelay: step.triggerFlowDelay || 0,
          isExpanded: false,
        })) || []
      );
    }
  }, [board]);

  // Create board mutation
  const createBoardMutation = useMutation({
    mutationFn: async () => {
      const newBoard = await boardsService.create({ name, description, color });
      // Create steps
      for (let i = 0; i < steps.length; i++) {
        const step = steps[i];
        await boardsService.createStep({
          boardId: newBoard.id,
          name: step.name,
          description: step.description || undefined,
          color: step.color,
          order: i,
          triggerFlowId: step.triggerFlowId || undefined,
          triggerFlowDelay: step.triggerFlowDelay || 0,
        });
      }
      return newBoard;
    },
    onSuccess: (board) => {
      queryClient.invalidateQueries({ queryKey: ['boards'] });
      toast({
        title: 'Quadro criado com sucesso!',
        variant: 'success',
      });
      navigate(`/app/quadros/${board.id}`);
    },
    onError: () => {
      toast({
        title: 'Erro ao criar quadro',
        variant: 'destructive',
      });
    },
  });

  // Update board mutation
  const updateBoardMutation = useMutation({
    mutationFn: async () => {
      await boardsService.update(id!, { name, description, color, active });

      // Handle steps - create new, update existing, delete removed
      const existingStepIds = board?.steps?.map((s) => s.id) || [];
      const currentStepIds = steps.filter((s) => s.id && !s.isNew).map((s) => s.id!);

      // Delete removed steps
      for (const stepId of existingStepIds) {
        if (!currentStepIds.includes(stepId)) {
          await boardsService.deleteStep(stepId);
        }
      }

      // Create or update steps
      for (let i = 0; i < steps.length; i++) {
        const step = steps[i];
        if (step.isNew || !step.id) {
          await boardsService.createStep({
            boardId: id!,
            name: step.name,
            description: step.description || undefined,
            color: step.color,
            order: i,
            triggerFlowId: step.triggerFlowId || undefined,
            triggerFlowDelay: step.triggerFlowDelay || 0,
          });
        } else {
          await boardsService.updateStep(step.id, {
            name: step.name,
            description: step.description || undefined,
            color: step.color,
            order: i,
            triggerFlowId: step.triggerFlowId,
            triggerFlowDelay: step.triggerFlowDelay || 0,
          });
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['boards'] });
      queryClient.invalidateQueries({ queryKey: ['board', id] });
      toast({
        title: 'Quadro atualizado com sucesso!',
        variant: 'success',
      });
    },
    onError: () => {
      toast({
        title: 'Erro ao atualizar quadro',
        variant: 'destructive',
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      toast({
        title: 'Nome obrigatorio',
        description: 'Informe um nome para o quadro',
        variant: 'destructive',
      });
      return;
    }

    if (isEditing) {
      updateBoardMutation.mutate();
    } else {
      createBoardMutation.mutate();
    }
  };

  const addStep = () => {
    setSteps([
      ...steps,
      {
        name: '',
        description: '',
        color: COLORS[steps.length % COLORS.length],
        triggerFlowId: null,
        triggerFlowDelay: 0,
        isNew: true,
        isExpanded: true,
      },
    ]);
  };

  const removeStep = (index: number) => {
    if (confirm('Tem certeza que deseja remover este passo?')) {
      setSteps(steps.filter((_, i) => i !== index));
    }
  };

  const updateStep = (index: number, field: keyof StepFormData, value: any) => {
    setSteps(
      steps.map((step, i) => (i === index ? { ...step, [field]: value } : step))
    );
  };

  const toggleStepExpanded = (index: number) => {
    setSteps(
      steps.map((step, i) => (i === index ? { ...step, isExpanded: !step.isExpanded } : step))
    );
  };

  const moveStep = (index: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= steps.length) return;

    const newSteps = [...steps];
    [newSteps[index], newSteps[newIndex]] = [newSteps[newIndex], newSteps[index]];
    setSteps(newSteps);
  };

  const isSubmitting = createBoardMutation.isPending || updateBoardMutation.isPending;

  if (isEditing && isLoadingBoard) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-gold" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/app/quadros')}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold text-brand-ivory">
            {isEditing ? 'Editar Quadro' : 'Novo Quadro'}
          </h1>
          <p className="text-brand-ivory/70 mt-1">
            Configure os passos do seu quadro Kanban
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Board Info */}
        <Card className="p-6 bg-brand-smoke border-brand-gold/20">
          <h2 className="text-xl font-semibold text-brand-ivory mb-4">Informacoes do Quadro</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nome *</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ex: Funil de Vendas"
                required
              />
            </div>

            <div className="space-y-2">
              <Label>Cor</Label>
              <div className="flex gap-2 flex-wrap">
                {COLORS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    className={`w-8 h-8 rounded-full border-2 transition-all ${
                      color === c ? 'border-white scale-110' : 'border-transparent'
                    }`}
                    style={{ backgroundColor: c }}
                    onClick={() => setColor(c)}
                  />
                ))}
              </div>
            </div>

            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="description">Descricao</Label>
              <textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Descricao do quadro (opcional)"
                rows={3}
                className="flex min-h-[80px] w-full rounded-md border border-brand-gold/20 bg-brand-ink px-3 py-2 text-sm text-brand-ivory placeholder:text-brand-ivory/50 focus:outline-none focus:ring-2 focus:ring-brand-gold/50 disabled:cursor-not-allowed disabled:opacity-50"
              />
            </div>

            {isEditing && (
              <div className="space-y-2">
                <Label>Status</Label>
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      checked={active}
                      onChange={() => setActive(true)}
                      className="accent-brand-gold"
                    />
                    <span className="text-brand-ivory">Ativo</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      checked={!active}
                      onChange={() => setActive(false)}
                      className="accent-brand-gold"
                    />
                    <span className="text-brand-ivory">Inativo</span>
                  </label>
                </div>
              </div>
            )}
          </div>
        </Card>

        {/* Steps */}
        <Card className="p-6 bg-brand-smoke border-brand-gold/20">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-xl font-semibold text-brand-ivory">Passos</h2>
              <p className="text-brand-ivory/60 text-sm">
                Os passos sao as colunas do seu quadro Kanban
              </p>
            </div>
            <Button type="button" variant="outline" onClick={addStep} className="gap-2">
              <Plus className="w-4 h-4" />
              Adicionar Passo
            </Button>
          </div>

          {steps.length === 0 ? (
            <div className="text-center py-8 text-brand-ivory/60">
              <p>Nenhum passo adicionado</p>
              <p className="text-sm mt-1">Clique em "Adicionar Passo" para comecar</p>
            </div>
          ) : (
            <div className="space-y-3">
              {steps.map((step, index) => (
                <div
                  key={step.id || `new-${index}`}
                  className="border border-brand-gold/20 rounded-lg overflow-hidden"
                >
                  {/* Step Header */}
                  <div
                    className="flex items-center gap-3 p-4 bg-brand-ink/50 cursor-pointer"
                    onClick={() => toggleStepExpanded(index)}
                  >
                    <div className="flex gap-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        onClick={(e) => {
                          e.stopPropagation();
                          moveStep(index, 'up');
                        }}
                        disabled={index === 0}
                      >
                        <ChevronUp className="w-4 h-4" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        onClick={(e) => {
                          e.stopPropagation();
                          moveStep(index, 'down');
                        }}
                        disabled={index === steps.length - 1}
                      >
                        <ChevronDown className="w-4 h-4" />
                      </Button>
                    </div>

                    <div
                      className="w-4 h-4 rounded-full flex-shrink-0"
                      style={{ backgroundColor: step.color }}
                    />

                    <span className="text-brand-ivory font-medium flex-1">
                      {step.name || `Passo ${index + 1}`}
                    </span>

                    {step.triggerFlowId && (
                      <span className="text-brand-gold text-xs flex items-center gap-1">
                        <Zap className="w-3 h-3" />
                        Dispara fluxo
                      </span>
                    )}

                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-red-400 hover:bg-red-400/10"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeStep(index);
                      }}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>

                    <ChevronDown
                      className={`w-5 h-5 text-brand-ivory/50 transition-transform ${
                        step.isExpanded ? 'rotate-180' : ''
                      }`}
                    />
                  </div>

                  {/* Step Details */}
                  {step.isExpanded && (
                    <div className="p-4 space-y-4 border-t border-brand-gold/10">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Nome do Passo *</Label>
                          <Input
                            value={step.name}
                            onChange={(e) => updateStep(index, 'name', e.target.value)}
                            placeholder="Ex: Novo Lead"
                            required
                          />
                        </div>

                        <div className="space-y-2">
                          <Label>Cor</Label>
                          <div className="flex gap-2 flex-wrap">
                            {COLORS.map((c) => (
                              <button
                                key={c}
                                type="button"
                                className={`w-6 h-6 rounded-full border-2 transition-all ${
                                  step.color === c ? 'border-white scale-110' : 'border-transparent'
                                }`}
                                style={{ backgroundColor: c }}
                                onClick={() => updateStep(index, 'color', c)}
                              />
                            ))}
                          </div>
                        </div>

                        <div className="space-y-2 md:col-span-2">
                          <Label>Descricao</Label>
                          <textarea
                            value={step.description}
                            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => updateStep(index, 'description', e.target.value)}
                            placeholder="Descricao do passo (opcional)"
                            rows={2}
                            className="flex min-h-[60px] w-full rounded-md border border-brand-gold/20 bg-brand-ink px-3 py-2 text-sm text-brand-ivory placeholder:text-brand-ivory/50 focus:outline-none focus:ring-2 focus:ring-brand-gold/50"
                          />
                        </div>
                      </div>

                      {/* Trigger Flow */}
                      <div className="border-t border-brand-gold/10 pt-4">
                        <h4 className="text-brand-ivory font-medium mb-3 flex items-center gap-2">
                          <Zap className="w-4 h-4 text-brand-gold" />
                          Automacao
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>Disparar fluxo ao entrar neste passo</Label>
                            <select
                              value={step.triggerFlowId || 'none'}
                              onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                                updateStep(index, 'triggerFlowId', e.target.value === 'none' ? null : e.target.value)
                              }
                              className="flex h-10 w-full rounded-md border border-brand-gold/20 bg-brand-ink px-3 py-2 text-sm text-brand-ivory focus:outline-none focus:ring-2 focus:ring-brand-gold/50"
                            >
                              <option value="none">Nenhum</option>
                              {flows?.map((flow) => (
                                <option key={flow.id} value={flow.id}>
                                  {flow.name}
                                </option>
                              ))}
                            </select>
                          </div>

                          {step.triggerFlowId && (
                            <div className="space-y-2">
                              <Label>Delay (minutos)</Label>
                              <Input
                                type="number"
                                min={0}
                                value={step.triggerFlowDelay}
                                onChange={(e) =>
                                  updateStep(index, 'triggerFlowDelay', parseInt(e.target.value) || 0)
                                }
                                placeholder="0 = imediato"
                              />
                            </div>
                          )}
                        </div>

                        {/* Actions Button */}
                        {step.id && !step.isNew && (
                          <div className="mt-4 pt-4 border-t border-brand-gold/10">
                            <Button
                              type="button"
                              variant="outline"
                              onClick={() => openActionsDialog(step.id!)}
                              className="w-full gap-2"
                            >
                              <Settings2 className="w-4 h-4" />
                              Configurar Acoes do Passo
                            </Button>
                            <p className="text-xs text-brand-ivory/50 mt-1">
                              Configure webhooks, emails, notificacoes e mais
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Actions */}
        <div className="flex justify-end gap-4">
          <Button type="button" variant="outline" onClick={() => navigate('/app/quadros')}>
            Cancelar
          </Button>
          <Button type="submit" disabled={isSubmitting} className="gap-2">
            <Save className="w-4 h-4" />
            {isSubmitting ? 'Salvando...' : isEditing ? 'Salvar Alteracoes' : 'Criar Quadro'}
          </Button>
        </div>
      </form>

      {/* Actions Dialog */}
      <Dialog open={actionsDialogOpen} onOpenChange={(open) => !open && closeActionsDialog()}>
        <DialogContent className="bg-brand-smoke border-brand-gold/20 text-brand-ivory max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-brand-gold flex items-center gap-2">
              <Settings2 className="w-5 h-5" />
              Acoes do Passo
            </DialogTitle>
            <DialogDescription className="text-brand-ivory/60">
              Configure acoes automaticas quando um cliente chegar neste passo
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 mt-4">
            {/* Existing Actions List */}
            {stepActions.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-medium text-brand-ivory">Acoes Configuradas</h4>
                <div className="space-y-2">
                  {stepActions.map((action) => {
                    const actionConfig = ACTION_TYPES.find((t) => t.type === action.actionType);
                    const Icon = actionConfig?.icon || Settings2;

                    return (
                      <div
                        key={action.id}
                        className={`flex items-center gap-3 p-3 rounded-lg border ${
                          action.active ? 'border-brand-gold/20 bg-brand-ink/50' : 'border-red-500/20 bg-red-500/5'
                        }`}
                      >
                        <Icon className={`w-5 h-5 ${actionConfig?.color || 'text-brand-ivory/60'}`} />
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-brand-ivory">{action.name}</span>
                            {!action.active && (
                              <span className="text-xs text-red-400 bg-red-400/10 px-2 py-0.5 rounded">
                                Desativado
                              </span>
                            )}
                            {action.delayMinutes > 0 && (
                              <span className="text-xs text-brand-ivory/50">
                                ({action.delayMinutes} min delay)
                              </span>
                            )}
                          </div>
                          {action.description && (
                            <p className="text-xs text-brand-ivory/50">{action.description}</p>
                          )}
                        </div>
                        <div className="flex gap-1">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => handleEditAction(action)}
                          >
                            <Settings2 className="w-4 h-4" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-red-400 hover:bg-red-400/10"
                            onClick={() => handleDeleteAction(action.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Action Form */}
            <div className="border-t border-brand-gold/10 pt-4">
              <h4 className="text-sm font-medium text-brand-ivory mb-4">
                {editingAction ? 'Editar Acao' : 'Nova Acao'}
              </h4>

              <div className="space-y-4">
                {/* Action Type */}
                <div className="space-y-2">
                  <Label>Tipo de Acao</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {ACTION_TYPES.map((type) => {
                      const Icon = type.icon;
                      return (
                        <button
                          key={type.type}
                          type="button"
                          onClick={() => setActionForm({ ...actionForm, actionType: type.type, config: {} })}
                          className={`flex items-center gap-2 p-3 rounded-lg border transition-all ${
                            actionForm.actionType === type.type
                              ? 'border-brand-gold bg-brand-gold/10'
                              : 'border-brand-gold/20 hover:border-brand-gold/40'
                          }`}
                        >
                          <Icon className={`w-5 h-5 ${type.color}`} />
                          <span className="text-brand-ivory text-sm">{type.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Name & Description */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Nome da Acao *</Label>
                    <Input
                      value={actionForm.name}
                      onChange={(e) => setActionForm({ ...actionForm, name: e.target.value })}
                      placeholder="Ex: Notificar equipe de vendas"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Delay (minutos)</Label>
                    <Input
                      type="number"
                      min={0}
                      value={actionForm.delayMinutes}
                      onChange={(e) => setActionForm({ ...actionForm, delayMinutes: parseInt(e.target.value) || 0 })}
                      placeholder="0 = imediato"
                    />
                  </div>
                </div>

                {/* Config based on action type */}
                {actionForm.actionType === 'WEBHOOK' && (
                  <div className="space-y-4 p-4 border border-brand-gold/20 rounded-lg">
                    <h5 className="text-sm font-medium text-brand-ivory">Configuracao do Webhook</h5>
                    <div className="space-y-2">
                      <Label>URL do Webhook *</Label>
                      <Input
                        value={actionForm.config.url || ''}
                        onChange={(e) => setActionForm({
                          ...actionForm,
                          config: { ...actionForm.config, url: e.target.value }
                        })}
                        placeholder="https://..."
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Metodo HTTP</Label>
                      <select
                        value={actionForm.config.method || 'POST'}
                        onChange={(e) => setActionForm({
                          ...actionForm,
                          config: { ...actionForm.config, method: e.target.value }
                        })}
                        className="flex h-10 w-full rounded-md border border-brand-gold/20 bg-brand-ink px-3 py-2 text-sm text-brand-ivory"
                      >
                        <option value="POST">POST</option>
                        <option value="GET">GET</option>
                        <option value="PUT">PUT</option>
                      </select>
                    </div>
                  </div>
                )}

                {actionForm.actionType === 'WHATSAPP' && (
                  <div className="space-y-4 p-4 border border-brand-gold/20 rounded-lg">
                    <h5 className="text-sm font-medium text-brand-ivory">Configuracao do WhatsApp</h5>
                    <div className="space-y-2">
                      <Label>Telefone de Destino *</Label>
                      <Input
                        value={actionForm.config.phone || ''}
                        onChange={(e) => setActionForm({
                          ...actionForm,
                          config: { ...actionForm.config, phone: e.target.value }
                        })}
                        placeholder="5511999999999"
                      />
                      <p className="text-xs text-brand-ivory/50">Use {'{{client.phone}}'} para telefone do cliente</p>
                    </div>
                    <div className="space-y-2">
                      <Label>Mensagem *</Label>
                      <textarea
                        value={actionForm.config.message || ''}
                        onChange={(e) => setActionForm({
                          ...actionForm,
                          config: { ...actionForm.config, message: e.target.value }
                        })}
                        placeholder="Olá! O cliente {{client.name}} chegou ao passo..."
                        rows={3}
                        className="flex w-full rounded-md border border-brand-gold/20 bg-brand-ink px-3 py-2 text-sm text-brand-ivory"
                      />
                      <p className="text-xs text-brand-ivory/50">
                        Variaveis: {'{{client.name}}'}, {'{{client.phone}}'}, {'{{client.email}}'}
                      </p>
                    </div>
                  </div>
                )}

                {actionForm.actionType === 'EMAIL' && (
                  <div className="space-y-4 p-4 border border-brand-gold/20 rounded-lg">
                    <h5 className="text-sm font-medium text-brand-ivory">Configuracao do Email</h5>
                    <div className="space-y-2">
                      <Label>Email de Destino *</Label>
                      <Input
                        value={actionForm.config.to || ''}
                        onChange={(e) => setActionForm({
                          ...actionForm,
                          config: { ...actionForm.config, to: e.target.value }
                        })}
                        placeholder="email@exemplo.com"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Assunto *</Label>
                      <Input
                        value={actionForm.config.subject || ''}
                        onChange={(e) => setActionForm({
                          ...actionForm,
                          config: { ...actionForm.config, subject: e.target.value }
                        })}
                        placeholder="Cliente {{client.name}} no passo..."
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Corpo do Email *</Label>
                      <textarea
                        value={actionForm.config.body || ''}
                        onChange={(e) => setActionForm({
                          ...actionForm,
                          config: { ...actionForm.config, body: e.target.value }
                        })}
                        placeholder="Detalhes do cliente..."
                        rows={4}
                        className="flex w-full rounded-md border border-brand-gold/20 bg-brand-ink px-3 py-2 text-sm text-brand-ivory"
                      />
                    </div>
                  </div>
                )}

                {actionForm.actionType === 'NOTIFICATION' && (
                  <div className="space-y-4 p-4 border border-brand-gold/20 rounded-lg">
                    <h5 className="text-sm font-medium text-brand-ivory">Configuracao da Notificacao</h5>
                    <div className="space-y-2">
                      <Label>Enviar para</Label>
                      <select
                        value={actionForm.config.targetType || 'ALL'}
                        onChange={(e) => setActionForm({
                          ...actionForm,
                          config: { ...actionForm.config, targetType: e.target.value, targetId: undefined }
                        })}
                        className="flex h-10 w-full rounded-md border border-brand-gold/20 bg-brand-ink px-3 py-2 text-sm text-brand-ivory"
                      >
                        <option value="ALL">Todos os usuarios</option>
                        <option value="USER">Usuario especifico</option>
                      </select>
                    </div>
                    {actionForm.config.targetType === 'USER' && (
                      <div className="space-y-2">
                        <Label>Usuario</Label>
                        <select
                          value={actionForm.config.targetId || ''}
                          onChange={(e) => setActionForm({
                            ...actionForm,
                            config: { ...actionForm.config, targetId: e.target.value }
                          })}
                          className="flex h-10 w-full rounded-md border border-brand-gold/20 bg-brand-ink px-3 py-2 text-sm text-brand-ivory"
                        >
                          <option value="">Selecione um usuario</option>
                          {usersData?.map((user) => (
                            <option key={user.id} value={user.id}>{user.name}</option>
                          ))}
                        </select>
                      </div>
                    )}
                    <div className="space-y-2">
                      <Label>Titulo *</Label>
                      <Input
                        value={actionForm.config.title || ''}
                        onChange={(e) => setActionForm({
                          ...actionForm,
                          config: { ...actionForm.config, title: e.target.value }
                        })}
                        placeholder="Cliente chegou ao passo"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Mensagem *</Label>
                      <textarea
                        value={actionForm.config.message || ''}
                        onChange={(e) => setActionForm({
                          ...actionForm,
                          config: { ...actionForm.config, message: e.target.value }
                        })}
                        placeholder="O cliente {{client.name}} chegou ao passo..."
                        rows={3}
                        className="flex w-full rounded-md border border-brand-gold/20 bg-brand-ink px-3 py-2 text-sm text-brand-ivory"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Link (opcional)</Label>
                      <Input
                        value={actionForm.config.link || ''}
                        onChange={(e) => setActionForm({
                          ...actionForm,
                          config: { ...actionForm.config, link: e.target.value }
                        })}
                        placeholder="/app/clientes/{{client.id}}"
                      />
                    </div>
                  </div>
                )}

                {actionForm.actionType === 'FLOW' && (
                  <div className="space-y-4 p-4 border border-brand-gold/20 rounded-lg">
                    <h5 className="text-sm font-medium text-brand-ivory">Configuracao do Fluxo</h5>
                    <div className="space-y-2">
                      <Label>Fluxo a Disparar *</Label>
                      <select
                        value={actionForm.config.flowId || ''}
                        onChange={(e) => setActionForm({
                          ...actionForm,
                          config: { ...actionForm.config, flowId: e.target.value }
                        })}
                        className="flex h-10 w-full rounded-md border border-brand-gold/20 bg-brand-ink px-3 py-2 text-sm text-brand-ivory"
                      >
                        <option value="">Selecione um fluxo</option>
                        {flows?.map((flow) => (
                          <option key={flow.id} value={flow.id}>{flow.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}

                {/* Active toggle */}
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="action-active"
                    checked={actionForm.active}
                    onChange={(e) => setActionForm({ ...actionForm, active: e.target.checked })}
                    className="accent-brand-gold"
                  />
                  <label htmlFor="action-active" className="text-sm text-brand-ivory">
                    Acao ativa
                  </label>
                </div>

                {/* Save Button */}
                <div className="flex gap-2">
                  {editingAction && (
                    <Button type="button" variant="outline" onClick={resetActionForm}>
                      Cancelar Edicao
                    </Button>
                  )}
                  <Button
                    type="button"
                    onClick={handleSaveAction}
                    disabled={createActionMutation.isPending || updateActionMutation.isPending}
                    className="gap-2"
                  >
                    <Save className="w-4 h-4" />
                    {editingAction ? 'Atualizar Acao' : 'Adicionar Acao'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
